import { detach, isNullOrUndefined, updateCSSText, EventHandler } from '@syncfusion/ej2-base';
import { DropDownButton } from '@syncfusion/ej2-splitbuttons';
import { ToolbarRenderer, ColorPickerRenderer } from './common/index';
import { getInlineToolbarItems, transformIntoToolbarItem, defaultTransformModel, getBlockModelById } from '../../common/utils/index';
import { events } from '../../common/constant';
import * as constants from '../../common/constant';
import { BlockType } from '../../models/enums';
/**
 * InlineToolbarModule class is used to render the inline toolbar for the block editor.
 *
 * @hidden
 */
export class InlineToolbarModule {
    constructor(editor) {
        this.currentTransformItems = [];
        this.editor = editor;
        this.toolbarRenderer = new ToolbarRenderer(this.editor);
        this.pickerHandler = new ColorPickerRenderer(this.editor);
        this.addEventListeners();
        this.init();
        this.bindTooltip();
    }
    addEventListeners() {
        this.editor.blockManager.observer.on('enableDisableTbarItems', this.enableDisableTbarItems, this);
        this.editor.on(events.inlineToolbarCreated, this.handleInlineToolbarCreated, this);
        this.editor.on(events.inlineToolbarItemClick, this.handleInlineToolbarItemClick, this);
        this.editor.on(events.moduleChanged, this.onPropertyChanged, this);
        this.editor.on(events.rtlChanged, this.applyRtlSettings, this);
        this.editor.on(events.destroy, this.destroy, this);
    }
    removeEventListeners() {
        this.editor.blockManager.observer.off('enableDisableTbarItems', this.enableDisableTbarItems);
        this.editor.off(events.inlineToolbarCreated, this.handleInlineToolbarCreated);
        this.editor.off(events.inlineToolbarItemClick, this.handleInlineToolbarItemClick);
        this.editor.off(events.moduleChanged, this.onPropertyChanged);
        this.editor.off(events.rtlChanged, this.applyRtlSettings);
        this.editor.off(events.destroy, this.destroy);
    }
    init() {
        this.toolbarEle = this.editor.createElement('div', {
            id: (this.editor.element.id + constants.BLOCKEDITOR_INLINETBAR_ID),
            className: constants.BLOCKEDITOR_INLINETBAR_CLS
        });
        this.editor.element.appendChild(this.toolbarEle);
        this.toolbarObj = this.renderToolbar({
            element: this.toolbarEle,
            items: this.getToolbarItems(this.editor.inlineToolbarSettings.items),
            width: this.editor.inlineToolbarSettings.popupWidth,
            overflowMode: 'MultiRow'
        });
        const popupElement = this.editor.createElement('div', {
            id: (this.editor.element.id + constants.INLINE_TBAR_POPUP_ID),
            className: constants.INLINE_TBAR_POPUP_CLS
        });
        this.toolbarObj.isStringTemplate = true;
        this.editor.element.appendChild(popupElement);
        this.editor.blockManager.observer.notify('toolbarCreated');
    }
    bindTooltip() {
        this.inlineToolbarTooltip = this.editor.tooltipRenderer.renderTooltip({
            cssClass: constants.INLINE_TBAR_TOOLTIP_CLS,
            position: 'TopCenter',
            target: '.' + constants.TBAR_ITEM_CLS,
            windowCollision: true,
            element: document.querySelector('#' + this.editor.element.id + constants.INLINE_TBAR_POPUP_ID)
        });
    }
    /**
     * Render the inline toolbar for the block editor.
     *
     * @param {IToolbarRenderOptions} args - The options for rendering the toolbar.
     * @returns {Toolbar} The rendered toolbar instance.
     * @hidden
     */
    renderToolbar(args) {
        return this.toolbarRenderer.renderToolbar(args);
    }
    handleInlineToolbarCreated() {
        if (this.hasToolbarItemId('color')) {
            this.initializeColorPicker('color');
        }
        if (this.hasToolbarItemId('bgColor')) {
            this.initializeColorPicker('bgColor');
        }
        if (this.hasToolbarItemId('transform')) {
            this.initializeTransformDropdown();
        }
    }
    handleInlineToolbarItemClick(args) {
        const itemClickArgs = {
            item: getInlineToolbarItems().find((item) => item.id === args.item.id),
            event: args.originalEvent,
            isInteracted: Object.keys(args.originalEvent).length > 0,
            cancel: false
        };
        if (this.editor.inlineToolbarSettings.itemClick) {
            this.editor.inlineToolbarSettings.itemClick.call(this.editor, itemClickArgs);
        }
        if (itemClickArgs.cancel) {
            args.cancel = true;
            return;
        }
        this.editor.blockManager.observer.notify('inlineToolbarItemClick', args);
    }
    enableDisableTbarItems(args) {
        this.toolbarObj.enableItems(args.items, args.isEnable);
    }
    initializeColorPicker(type) {
        const toolbarElement = document.querySelector('#' + this.editor.element.id + constants.BLOCKEDITOR_INLINETBAR_ID);
        if (!toolbarElement) {
            return;
        }
        const colorBtn = toolbarElement.querySelector(`#toolbar-${type.toLowerCase()}-dropdown`);
        // create and attach the ColorPicker popup directly to the toolbar button via handler
        const colorPicker = this.pickerHandler.renderColorPicker({
            element: colorBtn,
            type: type,
            iconCss: `e-inline-${type}-icon`,
            onChange: (value) => {
                this.editor.blockManager.observer.notify('handleColorpickerChange', { type, value });
            }
        });
        if (type === 'color') {
            this.textColorPicker = colorPicker;
        }
        else {
            this.bgColorPicker = colorPicker;
        }
    }
    createDropDown(args) {
        const dropDown = new DropDownButton({
            target: args.instance.target,
            items: args.instance.items,
            cssClass: args.instance.cssClass,
            popupWidth: args.instance.popupWidth,
            createPopupOnClick: args.instance.createPopupOnClick,
            select: args.instance.select,
            beforeOpen: args.instance.beforeOpen,
            itemTemplate: args.instance.itemTemplate
        });
        dropDown.appendTo(args.element);
        if (args.inlineClass) {
            const iconWrapper = this.editor.createElement('span', { className: 'e-be-color-icon-wrapper' });
            const iconElement = this.editor.createElement('span', { className: args.inlineClass });
            const cssText = 'border-bottom: 3px solid #000000;';
            updateCSSText(iconElement, cssText);
            iconWrapper.appendChild(iconElement);
            dropDown.element.insertBefore(iconWrapper, dropDown.element.querySelector('.e-caret'));
        }
        return dropDown;
    }
    applyRtlSettings() {
        if (this.toolbarObj) {
            this.toolbarObj.enableRtl = this.editor.enableRtl;
        }
        if (this.textColorPicker && this.pickerHandler) {
            this.textColorPicker.enableRtl = this.editor.enableRtl;
        }
        if (this.bgColorPicker && this.pickerHandler) {
            this.bgColorPicker.enableRtl = this.editor.enableRtl;
        }
    }
    /**
     * For internal use only - Get the module name.
     *
     * @returns {void}
     * @hidden
     */
    getModuleName() {
        return 'inlineToolbarSettings';
    }
    /**
     * Handles property changes to update the toolbar configuration
     *
     * @param {BlockEditorModel} e - specifies the element.
     * @returns {void}
     * @hidden
     */
    onPropertyChanged(e) {
        if (e.module === this.getModuleName()) {
            const newProp = e.newProp.inlineToolbarSettings;
            const oldProp = e.oldProp.inlineToolbarSettings;
            if (!isNullOrUndefined(newProp)) {
                for (const prop of Object.keys(newProp)) {
                    switch (prop) {
                        case 'popupWidth':
                            this.editor.blockManager.observer.notify('popupWidthChanged', { value: newProp.popupWidth });
                            break;
                        case 'items': {
                            const flattenedToolbarItems = this.getToolbarItems(newProp.items);
                            this.toolbarObj.items = flattenedToolbarItems;
                            setTimeout(() => {
                                this.handleInlineToolbarCreated();
                            }, 10);
                            break;
                        }
                    }
                }
            }
        }
        else {
            const moduleName = e.module;
            if (moduleName === 'fontColorSettings') {
                const fontModel = e.newProp.fontColorSettings;
                if (fontModel && this.textColorPicker && this.pickerHandler) {
                    this.pickerHandler.updatePickerProperties(this.textColorPicker, fontModel);
                }
                return;
            }
            if (moduleName === 'backgroundColorSettings') {
                const bgModel = e.newProp.backgroundColorSettings;
                if (bgModel && this.bgColorPicker && this.pickerHandler) {
                    this.pickerHandler.updatePickerProperties(this.bgColorPicker, bgModel);
                }
                return;
            }
        }
    }
    //Initializes the Transform dropdown (Paragraph, Headings, Lists)
    initializeTransformDropdown() {
        const toolbarElement = document.querySelector('#' + this.editor.element.id + constants.BLOCKEDITOR_INLINETBAR_ID);
        const transformBtn = toolbarElement.querySelector('#toolbar-transform-dropdown');
        const cssClass = 'e-flat e-be-blocktype-ddb e-blockeditor-command-menu';
        // Determine dropdown items: use custom transformSettings if provided, otherwise fall back to defaultTransformModel
        this.currentTransformItems = defaultTransformModel;
        if (this.editor.transformSettings &&
            this.editor.transformSettings.items &&
            this.editor.transformSettings.items.length > 0) {
            // Use custom transform items from transformSettings
            this.currentTransformItems = this.resolveTransformItems(this.editor.transformSettings.items);
        }
        const items = this.currentTransformItems.map((m) => ({
            type: m.type,
            id: m.id,
            label: m.label,
            iconCss: m.iconCss,
            shortcut: m.shortcut,
            disabled: m.disabled,
            tooltip: m.tooltip
        }));
        const selectHandler = (args) => {
            const itemId = args.item && args.item.id;
            if (!itemId) {
                return;
            }
            const model = this.currentTransformItems.find((m) => m.id === itemId);
            if (!model) {
                return;
            }
            // Respect disabled flag on transform items
            if (model.disabled) {
                return;
            }
            // Check if the selected item is the same as the current block type
            const currentItemId = this.getMatchingTransformItemId();
            if (currentItemId === itemId) {
                // Same block type selected, no need to transform
                return;
            }
            // Trigger itemSelect event if defined in transformSettings
            if (this.editor.transformSettings && this.editor.transformSettings.itemSelect) {
                const eventArgs = {
                    command: model,
                    element: args.item || null,
                    event: null,
                    cancel: false
                };
                this.editor.transformSettings.itemSelect.call(this.editor, eventArgs);
                if (eventArgs.cancel) {
                    return;
                }
            }
            let props;
            if (model.type === BlockType.Heading) {
                const match = itemId.match(/heading(\d)/i);
                const level = match ? Math.max(1, Math.min(4, parseInt(match[1], 10))) : 1;
                props = { level };
            }
            this.editor.blockManager.blockCommand.transformBlocksForSelection(model.type, props);
        };
        const beforeOpenHandler = () => {
            // Apply selected menu item styling when dropdown opens
            setTimeout(() => {
                this.applyTransformMenuSelection();
            }, 0);
        };
        // Create dropdown button with items and select handler
        const dropDown = this.createDropDown({
            instance: {
                items,
                cssClass: cssClass,
                createPopupOnClick: true,
                popupWidth: '250px',
                select: selectHandler,
                beforeOpen: beforeOpenHandler,
                itemTemplate: '<div class=\'e-transform-item-template\'><div class=\'e-transform-icon-info\'>' +
                    '<span class="${iconCss}"></span></div><div class=\'e-transform-item-info\'>' +
                    '<span title="${tooltip}">${label}' +
                    '</span><span class=\'e-be-transform-shortcut\'>${shortcut}</span></div></div>'
            },
            element: transformBtn,
            inlineClass: null,
            type: null
        });
        this.transformDDB = dropDown;
        // Initialize icon for the existing e-be-transform-block element
        const iconCss = this.getCurrentTransformIcon();
        const transformBlockElement = toolbarElement.querySelector('.e-be-transform-block');
        if (transformBlockElement) {
            // Keep base classes and add the icon class
            transformBlockElement.className = `e-be-transform-block ${iconCss}`;
        }
        // Update label live when selection/focus changes
        this.updateTransformLabelHandler = () => {
            const newIconCss = this.getCurrentTransformIcon();
            const hasIgnoredBlocks = this.hasIgnoredBlockTypes();
            if (this.transformDDB) {
                // Query select the e-be-transform-block element and update its icon
                const transformIcon = document.querySelector('.e-be-transform-block');
                if (transformIcon) {
                    // Update icon classes while keeping base classes
                    transformIcon.className = `e-be-transform-block ${newIconCss}`;
                }
                // Disable/enable the dropdown based on ignored block types
                this.transformDDB.disabled = hasIgnoredBlocks;
                // Add/remove e-disabled class to the transform button
                if (hasIgnoredBlocks) {
                    toolbarElement.firstElementChild.firstElementChild.classList.add('e-disabled');
                }
                else {
                    toolbarElement.firstElementChild.firstElementChild.classList.remove('e-disabled');
                }
            }
        };
        EventHandler.add(document, 'selectionchange', this.updateTransformLabelHandler);
        // Initialize disabled state based on current selection
        const hasIgnoredBlocks = this.hasIgnoredBlockTypes();
        if (this.transformDDB) {
            this.transformDDB.disabled = hasIgnoredBlocks;
            if (hasIgnoredBlocks) {
                toolbarElement.firstElementChild.firstElementChild.classList.add('e-disabled');
            }
        }
    }
    applyTransformMenuSelection() {
        // Get the current block's corresponding menu item
        const currentItemId = this.getMatchingTransformItemId();
        // Query all menu items in the dropdown popup - look for li elements with data-text attribute
        const menuPopup = document.querySelector('#toolbar-transform-dropdown-popup');
        if (!menuPopup) {
            return;
        }
        // Add e-selected class to the matching item only if it is not disabled
        if (currentItemId) {
            const matchingModel = this.currentTransformItems.find((m) => m.id === currentItemId);
            // Only add e-selected if the item is not disabled
            if (matchingModel && !matchingModel.disabled) {
                const selectedItem = menuPopup.querySelector(`#${currentItemId}`);
                if (selectedItem) {
                    selectedItem.classList.add('e-selected');
                }
            }
        }
    }
    getMatchingTransformItemId() {
        // If multiple blocks are selected, return null (no single item to highlight)
        const selectedBlocks = this.editor.getSelectedBlocks ? this.editor.getSelectedBlocks() : null;
        if (selectedBlocks && selectedBlocks.length > 1) {
            return null;
        }
        // Prefer selected block model when single block is selected
        let blockModel = null;
        if (selectedBlocks && selectedBlocks.length === 1) {
            blockModel = selectedBlocks[0];
        }
        else {
            const focusedBlk = this.editor.blockManager.currentFocusedBlock;
            if (!focusedBlk) {
                return null;
            }
            blockModel = getBlockModelById(focusedBlk.id, this.editor.blockManager.getEditorBlocks());
        }
        if (!blockModel) {
            return null;
        }
        const selType = blockModel.blockType;
        let model = null;
        if (selType === BlockType.Heading) {
            const level = blockModel.properties ?
                blockModel.properties.level || 1 : 1;
            model = this.currentTransformItems.find((m) => m.id === `heading${level}-command`);
        }
        if (!model) {
            model = this.currentTransformItems.find((m) => m.type === selType);
        }
        return model ? model.id : null;
    }
    getCurrentTransformIcon() {
        // Get default icon from first item (Paragraph)
        const defaultIcon = this.currentTransformItems.length > 0 ? this.currentTransformItems[0].iconCss : '';
        // If multiple blocks are selected, return paragraph icon
        const selectedBlocks = this.editor.getSelectedBlocks ? this.editor.getSelectedBlocks() : null;
        if (selectedBlocks && selectedBlocks.length > 1) {
            return defaultIcon;
        }
        // Prefer selected block model when single block is selected
        let blockModel = null;
        if (selectedBlocks && selectedBlocks.length === 1) {
            blockModel = selectedBlocks[0];
        }
        else {
            const focusedBlk = this.editor.blockManager.currentFocusedBlock;
            if (!focusedBlk) {
                return defaultIcon;
            }
            blockModel = getBlockModelById(focusedBlk.id, this.editor.blockManager.getEditorBlocks());
        }
        if (!blockModel) {
            return defaultIcon;
        }
        const selType = blockModel.blockType;
        let model = null;
        if (selType === BlockType.Heading) {
            const level = blockModel.properties ?
                blockModel.properties.level || 1 : 1;
            model = this.currentTransformItems.find((m) => m.id === `heading${level}-command`);
        }
        if (!model) {
            model = this.currentTransformItems.find((m) => m.type === selType);
        }
        return model ? model.iconCss : defaultIcon;
    }
    getToolbarItems(items) {
        const defaults = getInlineToolbarItems();
        const resolved = items.map((item) => {
            if (typeof item === 'string') {
                const match = defaults.find((d) => d.command && d.command.toLowerCase() === item.toLowerCase());
                return match ? match : item;
            }
            return item;
        });
        return transformIntoToolbarItem(resolved);
    }
    hasToolbarItemId(targetId) {
        const items = this.getToolbarItems(this.editor.inlineToolbarSettings.items);
        return items.some((item) => {
            const id = item.id;
            return id && id.toLowerCase() === targetId.toLowerCase();
        });
    }
    hasIgnoredBlockTypes() {
        // Check if any selected blocks have ignored types
        const selectedBlocks = this.editor.getSelectedBlocks ? this.editor.getSelectedBlocks() : null;
        const ignoredTypes = [BlockType.Callout, BlockType.Quote, BlockType.Image, BlockType.Divider, BlockType.Code];
        // If blocks are selected, check if any are of ignored type
        if (selectedBlocks && selectedBlocks.length > 0) {
            return selectedBlocks.some((block) => ignoredTypes.indexOf(block.blockType) !== -1);
        }
        // If no selected blocks, check the currently focused block
        const focusedBlk = this.editor.blockManager.currentFocusedBlock;
        if (!focusedBlk) {
            return false;
        }
        const blockModel = getBlockModelById(focusedBlk.id, this.editor.blockManager.getEditorBlocks());
        if (!blockModel) {
            return false;
        }
        return ignoredTypes.indexOf(blockModel.blockType) !== -1;
    }
    /**
     * Resolves custom transform items from transformSettings into a normalized TransformItemModel array.
     * Handles string identifiers and model objects.
     *
     * @param {(string | TransformCommandName | TransformItemModel)[]} items - The raw transform items.
     * @returns {TransformItemModel[]} - The resolved transform items.
     * @private
     */
    resolveTransformItems(items) {
        return items
            .map((item) => {
            // Handle object models
            if (typeof item === 'object' && item !== null) {
                return item;
            }
            // Handle string identifiers - find matching item from defaultTransformModel
            const itemStr = String(item).trim().toLowerCase();
            return defaultTransformModel.find((m) => m.label.toLowerCase() === itemStr);
        })
            .filter((item) => item);
    }
    /**
     * Destroys the inline toolbar module and cleans up resources
     *
     * @returns {void}
     */
    destroy() {
        this.removeEventListeners();
        if (this.textColorPicker) {
            this.textColorPicker.destroy();
            this.textColorPicker = null;
        }
        if (this.bgColorPicker) {
            this.bgColorPicker.destroy();
            this.bgColorPicker = null;
        }
        if (this.inlineToolbarTooltip) {
            this.inlineToolbarTooltip.destroy();
        }
        if (this.toolbarObj) {
            this.toolbarObj.destroy();
            detach(this.toolbarEle);
            this.toolbarObj = null;
        }
        if (this.updateTransformLabelHandler) {
            EventHandler.remove(document, 'selectionchange', this.updateTransformLabelHandler);
            this.updateTransformLabelHandler = null;
        }
        this.toolbarRenderer = null;
        this.inlineToolbarTooltip = null;
        this.pickerHandler = null;
    }
}
